import type { UseTuningSession } from '../hooks/useTuningSession';
import { Card } from '@/components/ui/Card';
import { GoalCard, TipCard, WarningCard, InfoCard } from '../components/Cards';

interface CableDrivenData {
  /** Position of cord clamp on the buss cable. */
  clampPosition?: 'near-bottom-cam' | 'mid' | 'near-top-cam';
  /** Launcher up-percentage from observation. */
  guidancePercent?: number;
  contactClean?: boolean;
}

interface Props { tuning: UseTuningSession }

/**
 * Step 6b — Cable-Driven Rest Setup
 *
 * Setup procedure:
 *   1. Mount rest on the riser (5/16 cap-head screw, Berger hole)
 *   2. Clamp activation cord to the DOWN-traveling buss cable
 *      (the cable that moves AWAY from the shooter during draw)
 *   3. Position clamp on the cable to control timing
 *   4. Verify 50-70% guidance like limb-driven
 */
export function RestCableDrivenStep({ tuning }: Props) {
  const data = (tuning.session.stepData['rest_cable_driven'] as CableDrivenData | undefined) ?? {};
  const update = (patch: Partial<CableDrivenData>) =>
    tuning.updateStepData<CableDrivenData>('rest_cable_driven', { ...data, ...patch });

  const inGuidanceRange = data.guidancePercent !== undefined
    && data.guidancePercent >= 50 && data.guidancePercent <= 70;
  const guidanceLow = data.guidancePercent !== undefined && data.guidancePercent < 50;
  const guidanceHigh = data.guidancePercent !== undefined && data.guidancePercent > 70;

  return (
    <>
      <GoalCard title="What we're trying to achieve">
        The launcher should be UP and supporting the arrow for 50-70% of the power
        stroke. Cable-driven rests time off cable travel — clamp position on the cable
        controls when the rest reaches full-up.
      </GoalCard>

      <Card padding="lg">
        <h3 className="font-semibold mb-2">Install procedure</h3>
        <ol className="list-decimal list-inside space-y-1.5 text-[13px] text-zinc-300 leading-relaxed">
          <li>
            Press the bow. You need slack in the cables to attach the cord cleanly.
          </li>
          <li>
            Mount the rest to the riser via the Berger hole using the included 5/16" screw.
            Snug but not torqued — final torque comes after center shot is dialed.
          </li>
          <li>
            Identify the DOWN-traveling buss cable — this is the one that moves AWAY from the
            shooter (toward the cams) as the bow is drawn. Cable-driven rests must attach
            here, not the up-traveling cable.
          </li>
          <li>
            Wrap the activation cord around the down cable and clamp it. Spring tension
            should hold the launcher DOWN at brace; cable tension as the bow is drawn
            pulls it UP.
          </li>
          <li>Release press. Verify with a slow draw — launcher rises smoothly to full-up.</li>
        </ol>
      </Card>

      <Card padding="lg">
        <h3 className="font-semibold mb-3">Cord clamp position on the cable</h3>
        <p className="text-[12px] text-zinc-400 mb-3 leading-relaxed">
          Clamp position on the cable controls timing. Closer to the cam = more cable
          travel per unit of draw = rest rises faster. Closer to mid-cable = slower rise.
        </p>
        <div className="space-y-2">
          <Choice
            label="Near the BOTTOM cam (early rise, long stays up)"
            selected={data.clampPosition === 'near-bottom-cam'}
            onClick={() => update({ clampPosition: 'near-bottom-cam' })}
          />
          <Choice
            label="Mid-cable (balanced — recommended starting position)"
            selected={data.clampPosition === 'mid'}
            onClick={() => update({ clampPosition: 'mid' })}
          />
          <Choice
            label="Near the TOP cam (late rise, short stays up)"
            selected={data.clampPosition === 'near-top-cam'}
            onClick={() => update({ clampPosition: 'near-top-cam' })}
          />
        </div>
      </Card>

      <Card padding="lg">
        <h3 className="font-semibold mb-2">Guidance check</h3>
        <p className="text-[12px] text-zinc-400 mb-3 leading-relaxed">
          Slow-draw and observe (draw board ideal). Roughly what percentage of the power
          stroke is the launcher UP? Aim for 50-70%.
        </p>
        <Field label="% of power stroke launcher is UP">
          <input
            type="number"
            min={0}
            max={100}
            step={5}
            value={data.guidancePercent ?? ''}
            onChange={(e) => update({ guidancePercent: parseFloatOrUndef(e.target.value) })}
            placeholder="50-70"
            className={inputClass}
          />
        </Field>
        {data.guidancePercent !== undefined && (
          <div className="mt-3">
            {inGuidanceRange ? (
              <Status ok>{data.guidancePercent}% — in the sweet spot.</Status>
            ) : guidanceLow ? (
              <Status>
                Only {data.guidancePercent}% — rest coming up too late. Slide cord clamp TOWARD the
                bottom cam (closer to cam = more cable travel = earlier rise). Bow press needed.
              </Status>
            ) : guidanceHigh ? (
              <Status>
                {data.guidancePercent}% — rest is up too long; risk of fletching contact. Slide cord
                clamp TOWARD the top cam (closer to mid-cable / top = later rise).
              </Status>
            ) : null}
          </div>
        )}
      </Card>

      <Card padding="lg">
        <h3 className="font-semibold mb-2">Fletching contact check</h3>
        <p className="text-[12px] text-zinc-400 mb-3 leading-relaxed">
          Spray rest + cable with foot powder. Shoot one arrow. Streak marks = contact.
        </p>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!data.contactClean}
            onChange={(e) => update({ contactClean: e.target.checked })}
            className="mt-1 w-4 h-4 accent-ember-500"
          />
          <span className="text-sm">No fletching contact — clean powder pattern</span>
        </label>
      </Card>

      <TipCard title="Cable creep means re-timing">
        <p className="mb-2">
          Cables stretch ~1/8" in the first 100 shots after install, then more slowly.
          As they stretch, the cord clamp's effective position shifts and rest timing
          drifts. Plan to re-check timing:
        </p>
        <ul className="list-disc list-inside space-y-0.5 text-zinc-300">
          <li>After the first 100 shots on a new string set</li>
          <li>After any draw weight change &gt; 5 lb</li>
          <li>Every season as a routine</li>
        </ul>
        <p className="text-[11px] text-zinc-400 mt-2">
          If your rest used to be at 60% guidance and is now at 40%, slide the clamp 1/16"
          toward the cam — usually restores timing exactly.
        </p>
      </TipCard>

      <InfoCard title="Why cable-driven is preferred by target archers">
        <p>
          The cable's travel is smoother + more linear than limb flex, so the launcher
          rises in a more controlled arc. For indoor target shooters chasing the last 1%
          of consistency, cable-driven wins. For hunters in the field where field repair
          and fail-safe matter more, limb-driven wins.
        </p>
      </InfoCard>

      <WarningCard title="Safety reminder">
        Use only the cord material that came with your rest. NEVER wrap the cord around
        the SERVING of the cable — clamp on the cable itself. Inspect cord every 200
        shots; replace if it shows any abrasion at the clamp point.
      </WarningCard>
    </>
  );
}

const inputClass =
  'w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-ember-500';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label className="text-[11px] text-zinc-500 block mb-1">{label}</label>{children}</div>;
}

function Choice({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={
        'w-full text-left rounded-md border px-3 py-2.5 text-sm transition ' +
        (selected
          ? 'border-ember-500 bg-ember-500/10 text-ember-500 font-semibold'
          : 'border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-zinc-700')
      }
    >
      {label}
    </button>
  );
}

function Status({ children, ok }: { children: React.ReactNode; ok?: boolean }) {
  return (
    <div className={`text-sm leading-relaxed p-2.5 rounded border ${ok ? 'border-emerald-700/40 bg-emerald-950/20 text-emerald-300' : 'border-amber-700/40 bg-amber-950/20 text-amber-300'}`}>
      {children}
    </div>
  );
}

function parseFloatOrUndef(s: string): number | undefined {
  if (s === '') return undefined;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : undefined;
}
